import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar, MapPin, Users, ArrowRight, ArrowLeft, Lock, Unlock, Tag, Package, Plus, Minus, Send, CheckCircle } from 'lucide-react';
import { getProviderFromMethod } from '../components/PaymentProviderLogo';
import EventHero from '../components/event-page/EventHero';
import EventDetailsStrip from '../components/event-page/EventDetailsStrip';
import EventSidebar from '../components/event-page/EventSidebar';
import ExistingPassBanner from '../components/event-page/ExistingPassBanner';
import ClaimSuccessCard from '../components/event-page/ClaimSuccessCard';
import PaymentMethodCard from '../components/event-page/PaymentMethodCard';
import PaymentSummaryCard from '../components/event-page/PaymentSummaryCard';
import DirectTransferSteps from '../components/event-page/DirectTransferSteps';
import ClaimAccountCard from '../components/event-page/ClaimAccountCard';
import OrderConfirmationCard from '../components/event-page/OrderConfirmationCard';
import { formatEventDateTime, formatEventTimeRange } from '../utils/dateFormat';
import { getEventPersonality } from '../lib/eventPersonality';
import { resolveEntryMode } from '../lib/entryFlow';
import { resolveSourceOptions } from '../lib/sourceOptions';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Normalize a legacy payment link for consistent rendering.
 * Handles: missing provider, handles stored as URLs, Zelle mistyped as 'link'.
 */
function normalizePaymentLink(link) {
  const n = { ...link };
  const provider = n.payment_provider || getProviderFromMethod(n.method);
  n.payment_provider = provider;

  // Zelle should always be 'direct', regardless of stored type
  if (provider === 'zelle') {
    n.payment_method_type = 'direct';
  }

  // Detect handles stored as URLs (legacy: url = "@handle" or "$handle")
  if (!n.handle && n.url) {
    const url = n.url.trim();
    if (provider === 'venmo' && (url.startsWith('@') || (!url.startsWith('http') && !url.includes('/')))) {
      n.handle = url;
      n.url = `https://venmo.com/${url.replace('@', '')}`;
    } else if (provider === 'cashapp' && (url.startsWith('$') || (!url.startsWith('http') && !url.includes('/')))) {
      n.handle = url;
      n.url = `https://cash.app/${url.startsWith('$') ? url : '$' + url}`;
    } else if (provider === 'paypal' && !url.startsWith('http')) {
      n.handle = url;
      n.url = `https://paypal.me/${url}`;
    }
  }

  // Extract handle from known URL patterns if handle is still missing
  if (!n.handle && n.url) {
    const url = n.url;
    if (provider === 'venmo') {
      const match = url.match(/venmo\.com\/([^/?]+)/);
      if (match) n.handle = '@' + match[1];
    } else if (provider === 'cashapp') {
      const match = url.match(/cash\.app\/(\$?[^/?]+)/);
      if (match) n.handle = match[1].startsWith('$') ? match[1] : '$' + match[1];
    } else if (provider === 'paypal') {
      const match = url.match(/paypal\.me\/([^/?]+)/);
      if (match) n.handle = match[1];
    }
  }

  // Ensure URLs start with http for link-based methods
  if (n.url && !n.url.startsWith('http') && n.payment_method_type !== 'direct') {
    n.url = 'https://' + n.url;
  }

  return n;
}

export default function EventPage() {
  const { event_id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1);
  const [selectedTier, setSelectedTier] = useState(null);
  const [entryQuantity, setEntryQuantity] = useState(1);
  const [form, setForm] = useState({
    guest_name: '',
    guest_email: '',
    guest_legal_name: '',
    payment_confirmation: '',
    payment_method_used: '',
    how_heard_about: '',
    needs_tour: false
  });
  const [showLegalName, setShowLegalName] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [proofFile, setProofFile] = useState(null);
  const [proofPreview, setProofPreview] = useState(null);
  const [uploadingProof, setUploadingProof] = useState(false);
  const [emailChecked, setEmailChecked] = useState(null);
  const [purchaseComplete, setPurchaseComplete] = useState(null);
  const [claimForm, setClaimForm] = useState({ screen_name: '', password: '' });
  const [claiming, setClaiming] = useState(false);
  const [accountClaimed, setAccountClaimed] = useState(false);
  const [existingPass, setExistingPass] = useState(null);

  // Access code state
  const [accessCodeInputs, setAccessCodeInputs] = useState({});
  const [unlockedTiers, setUnlockedTiers] = useState({});
  const [validatingAccess, setValidatingAccess] = useState({});

  // Discount code state
  const [discountCode, setDiscountCode] = useState('');
  const [discountResult, setDiscountResult] = useState(null);
  const [validatingDiscount, setValidatingDiscount] = useState(false);

  // Add-on selection state: { [addonId]: quantity }
  const [selectedAddOns, setSelectedAddOns] = useState({});

  // Smart Entry Flow — resolved phase (null when entry_mode != 'smart_flow')
  const activePhase = event ? resolveEntryMode(event) : null;
  const isCashRsvpMode = activePhase?.mode === 'cash_rsvp';

  // Scroll to top on step changes and account claim
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step, purchaseComplete, accountClaimed]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success('Copied to clipboard')).catch(() => {});
  };

  const hasDirectMethods = () => event?.payment_links?.some(l => l.payment_method_type === 'direct');

  const copyAllPaymentDetails = (link) => {
    const total = getDisplayPrice();
    const lines = [`${link.method} Payment Details`];
    if (link.recipient_name) lines.push(`Recipient: ${link.recipient_name}`);
    if (link.email_or_phone) lines.push(`${link.method}: ${link.email_or_phone}`);
    lines.push(`Amount: $${total.toFixed(2)}`);
    if (link.suggested_memo) lines.push(`Memo: ${link.suggested_memo}`);
    navigator.clipboard.writeText(lines.join('\n')).then(() => toast.success('Payment details copied')).catch(() => {});
  };

  // Shared local field-copy helper used by PaymentMethodCard.
  const copyField = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const renderPaymentMethod = (link) => (
    <PaymentMethodCard
      key={link.id || link.method}
      link={link}
      onCopyAll={() => copyAllPaymentDetails(link)}
      onCopyField={copyField}
    />
  );

  const renderPaymentSummary = () => (
    <PaymentSummaryCard
      event={event}
      selectedTier={selectedTier}
      entryQuantity={entryQuantity}
      selectedAddOns={selectedAddOns}
      activeAddOns={activeAddons}
      discountResult={discountResult}
      displayPrice={getDisplayPrice()}
    />
  );

  const renderDirectTransferSteps = () => {
    if (!hasDirectMethods()) return null;
    return <DirectTransferSteps event={event} displayPrice={getDisplayPrice()} />;
  };

  const fetchEvent = useCallback(async () => {
    try {
      const [eventRes, availRes] = await Promise.all([
        axios.get(`${API}/events/${event_id}`),
        axios.get(`${API}/events/${event_id}/availability`)
      ]);
      const eventData = eventRes.data;
      // Normalize legacy payment links for consistent rendering
      if (eventData.payment_links) {
        eventData.payment_links = eventData.payment_links.map(normalizePaymentLink);
      }
      setEvent(eventData);
      setAvailability(availRes.data);
    } catch (error) {
      toast.error('Event not found');
      navigate('/');
    } finally {
      setLoading(false);
    }
  }, [event_id, navigate]);

  useEffect(() => {
    fetchEvent();
  }, [fetchEvent]);

  // Check if logged-in user already has a pass for this event
  useEffect(() => {
    const token = localStorage.getItem('session_token');
    if (!token) return;
    axios.get(`${API}/users/my-tickets`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const match = res.data.find(t => t.event_id === event_id && t.status !== 'revoked');
        if (match) setExistingPass(match);
      })
      .catch(() => {});
  }, [event_id]);

  const handleValidateAccessCode = async (tierId) => {
    const code = accessCodeInputs[tierId];
    if (!code) { toast.error('Please enter an access code'); return; }
    setValidatingAccess(prev => ({ ...prev, [tierId]: true }));
    try {
      await axios.post(`${API}/events/${event_id}/validate-access-code`, {
        tier_id: tierId,
        access_code: code
      });
      setUnlockedTiers(prev => ({ ...prev, [tierId]: true }));
      toast.success('Access code accepted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid access code');
    } finally {
      setValidatingAccess(prev => ({ ...prev, [tierId]: false }));
    }
  };

  const handleValidateDiscount = async () => {
    if (!discountCode.trim()) { toast.error('Enter a discount code'); return; }
    setValidatingDiscount(true);
    try {
      const res = await axios.post(`${API}/events/${event_id}/validate-discount`, {
        code: discountCode,
        tier_id: selectedTier?.id || null,
        entry_quantity: entryQuantity,
        add_ons_total: getAddOnsTotal()
      });
      setDiscountResult(res.data);
      toast.success(`Discount applied: -$${res.data.discount_amount}`);
    } catch (error) {
      setDiscountResult(null);
      toast.error(error.response?.data?.detail || 'Invalid discount code');
    } finally {
      setValidatingDiscount(false);
    }
  };

  const handleSubmitTicket = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Stripe Checkout path: only when the user explicitly selected Stripe in
      // Step 2 (sets form.payment_method_used='Stripe'). Webhook fulfills the
      // ticket — we never POST /tickets here. cash_rsvp + manual payments + comp
      // all skip Stripe and go through the regular /api/tickets flow.
      const isStripePath = !!selectedTier && !selectedTier.is_comp
        && getDisplayPrice() > 0
        && !isCashRsvpMode
        && form.payment_method_used === 'Stripe';
      if (isStripePath) {
        const stripePayload = {
          event_id: event_id,
          pass_tier_id: selectedTier?.id,
          guest_name: form.guest_name,
          guest_email: form.guest_email,
          guest_display_name: form.guest_name,
          guest_legal_name: form.guest_legal_name || '',
          entry_quantity: entryQuantity,
          add_ons: Object.entries(selectedAddOns)
            .filter(([, qty]) => qty > 0)
            .map(([id, qty]) => ({ add_on_id: id, quantity: qty })),
          origin_url: window.location.origin,
        };
        if (selectedTier?.requires_access_code && accessCodeInputs[selectedTier.id]) {
          stripePayload.access_code = accessCodeInputs[selectedTier.id];
        }
        if (discountResult?.valid) {
          stripePayload.discount_code = discountResult.code;
        }
        const res = await axios.post(`${API}/payments/create-checkout-session`, stripePayload);
        if (res.data?.checkout_url) {
          window.location.href = res.data.checkout_url;
          return;
        }
        throw new Error('No checkout URL returned');
      }

      let proofImageUrl = null;
      if (proofFile) {
        setUploadingProof(true);
        const fd = new FormData();
        fd.append('file', proofFile);
        const uploadRes = await axios.post(`${API}/upload-proof`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        proofImageUrl = uploadRes.data.url;
        setUploadingProof(false);
      }
      const payload = {
        event_id: event_id,
        pass_tier_id: selectedTier?.id || null,
        entry_quantity: entryQuantity,
        guest_name: form.guest_name,
        guest_email: form.guest_email,
        guest_display_name: form.guest_name,
        guest_legal_name: form.guest_legal_name || '',
        payment_confirmation: form.payment_confirmation,
        payment_method_used: form.payment_method_used,
        how_heard_about: form.how_heard_about || null,
        needs_tour: !!form.needs_tour,
        add_ons: Object.entries(selectedAddOns)
          .filter(([, qty]) => qty > 0)
          .map(([id, qty]) => ({ add_on_id: id, quantity: qty }))
      };
      if (selectedTier?.requires_access_code && accessCodeInputs[selectedTier.id]) {
        payload.access_code = accessCodeInputs[selectedTier.id];
      }
      if (discountResult?.valid) {
        payload.discount_code = discountResult.code;
      }
      if (proofImageUrl) {
        payload.payment_proof_image = proofImageUrl;
      }
      const response = await axios.post(`${API}/tickets`, payload);
      const _isManualEv = event?.release_method === 'manual';
      toast.success(
        _isManualEv
          ? 'Request submitted. Check your email for confirmation.'
          : 'Pass secured! Check your email for details.'
      );
      setPurchaseComplete({ ticket_id: response.data.ticket_id, email: form.guest_email });
      // Already-logged-in users already have an account — skip the claim-account
      // prompt and take them straight to the success card.
      if (localStorage.getItem('session_token')) {
        setAccountClaimed(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to secure your pass');
    } finally {
      setSubmitting(false);
    }
  };

  // Entry tiers and addon tiers from availability
  const activeTiers = (availability?.tiers || []).filter(t => t.status === 'active');

  const handleCheckEmail = async (email) => {
    if (!email || !email.includes('@')) return;
    try {
      const res = await axios.post(`${API}/auth/check-email`, { email });
      setEmailChecked(res.data);
    } catch { setEmailChecked(null); }
  };

  const handleClaimAccount = async () => {
    if (!claimForm.password || claimForm.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setClaiming(true);
    try {
      const res = await axios.post(`${API}/auth/claim-account`, {
        email: purchaseComplete.email,
        password: claimForm.password,
        screen_name: claimForm.screen_name || undefined
      });
      localStorage.setItem('session_token', res.data.session_token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      toast.success('Account created! Your passes are saved.');
      setAccountClaimed(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create account');
    } finally { setClaiming(false); }
  };
  const activeAddons = (availability?.addons || []).filter(a => a.status === 'active');
  const hasTiers = activeTiers.length > 0;

  const handleTierSelect = (tier) => {
    if (tier.requires_access_code && !unlockedTiers[tier.id]) return;
    setSelectedTier(tier);
    setEntryQuantity(tier.allow_quantity_selection === false ? 1 : (tier.min_quantity || 1));
    setDiscountCode('');
    setDiscountResult(null);
  };

  // Add-on quantity helpers
  const updateAddonQty = (addonId, delta) => {
    const addon = activeAddons.find(a => a.id === addonId);
    setSelectedAddOns(prev => {
      const current = prev[addonId] || 0;
      const minQty = addon?.min_quantity || 0;
      const next = Math.max(0, current + delta);
      const updated = { ...prev };
      if (next === 0) { delete updated[addonId]; } else { updated[addonId] = next; }
      return updated;
    });
    setDiscountResult(null);
  };

  // Toggle fixed-qty add-on (on/off at qty 1)
  const toggleFixedAddon = (addonId) => {
    setSelectedAddOns(prev => {
      const updated = { ...prev };
      if (updated[addonId]) { delete updated[addonId]; } else { updated[addonId] = 1; }
      return updated;
    });
    setDiscountResult(null);
  };

  const getAddOnsTotal = () => Object.entries(selectedAddOns).reduce((sum, [id, qty]) => {
    const ao = activeAddons.find(a => a.id === id);
    return sum + (ao ? ao.price * qty : 0);
  }, 0);

  // Price calculation
  const getEntrySubtotal = () => {
    if (!selectedTier || selectedTier.is_comp) return 0;
    return (selectedTier.price || 0) * entryQuantity;
  };

  const getDisplayPrice = () => {
    const entryTotal = getEntrySubtotal();
    const isComp = selectedTier?.is_comp;
    const fee = 0;
    const addOnsTotal = isComp ? 0 : getAddOnsTotal();
    const base = entryTotal + fee + addOnsTotal;
    if (discountResult?.valid) {
      return Math.max(0, base - discountResult.discount_amount);
    }
    return base;
  };

  // Check if entry tier has remaining quantity for the requested qty
  const getMaxEntryQty = () => {
    if (!selectedTier) return 1;
    let max = 99;
    if (selectedTier.remaining !== -1) max = Math.max(1, selectedTier.remaining);
    if (selectedTier.max_quantity) max = Math.min(max, selectedTier.max_quantity);
    return max;
  };

  const getMinEntryQty = () => {
    if (!selectedTier) return 1;
    return selectedTier.min_quantity || 1;
  };

  const getMaxAddonQty = (addon) => {
    let max = 99;
    if (addon.remaining !== -1) max = Math.max(0, addon.remaining);
    if (addon.max_quantity) max = Math.min(max, addon.max_quantity);
    return max;
  };

  const proceedToPayment = () => {
    if (!selectedTier && hasTiers) {
      toast.error('Please select an entry pass');
      return;
    }
    // Smart Flow: cash_rsvp skips payment-selection entirely (no payment to pick).
    // Comp tiers also skip when free (still routes through Step 2 today for the
    // explicit "No payment required" affordance — leave that path unchanged).
    if (isCashRsvpMode) {
      setForm(prev => ({ ...prev, payment_method_used: 'RSVP' }));
      setStep(3);
      return;
    }
    setStep(2);
  };

  if (loading || !event) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-[#050505] ${getEventPersonality(event.event_type).themeClass}`} data-testid="event-page-root">
      {/* Hero */}
      <EventHero event={event} />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Event Details */}
          <div className="lg:col-span-2 space-y-8">
            <EventDetailsStrip event={event} availability={availability} />

            {/* Step 1: Entry Selection + Add-Ons */}
            {step === 1 && (
              <>
                {/* Existing pass banner */}
                <ExistingPassBanner
                  existingPass={existingPass}
                  onViewExistingPass={(p) => navigate(`/ticket/${p.ticket_id}`)}
                />
                {/* SECTION 1: Select Your Entry */}
                {hasTiers ? (
                  <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-6" data-testid="tier-selection">
                    <h3 className="text-lg font-semibold text-[#EAE7E1] mb-4 tracking-tight">Select Your Entry</h3>
                    <div className="space-y-3">
                      {activeTiers.map((tier) => {
                        const isSoldOut = tier.remaining === 0;
                        const isLocked = tier.requires_access_code && !unlockedTiers[tier.id];
                        const isSelected = selectedTier?.id === tier.id;
                        return (
                          <div key={tier.id} data-testid={`tier-option-${tier.id}`}>
                            <button
                              type="button"
                              disabled={isSoldOut || isLocked}
                              onClick={() => handleTierSelect(tier)}
                              className={`w-full text-left border-2 rounded-sm p-4 transition-colors ${
                                isSelected ? 'border-[#D4AF37] bg-[#D4AF37]/5' :
                                isSoldOut ? 'border-[#1A1A1A] bg-[#0A0A0A] opacity-40 cursor-not-allowed' :
                                isLocked ? 'border-[#1A1A1A] bg-[#0A0A0A] cursor-default' :
                                'border-[#1A1A1A] hover:border-[#D4AF37] cursor-pointer'
                              }`}
                            >
                              <div className="flex justify-between items-center">
                                <div className="flex items-center">
                                  {/* Radio indicator */}
                                  <div className={`w-5 h-5 rounded-full border-2 mr-3 flex-shrink-0 flex items-center justify-center ${isSelected ? 'border-[#D4AF37]' : 'border-[#555]'}`}>
                                    {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-[#D4AF37]" />}
                                  </div>
                                  {isLocked && <Lock className="w-5 h-5 text-[#AFAFAF] mr-3 flex-shrink-0" />}
                                  {!isLocked && tier.requires_access_code && <Unlock className="w-5 h-5 text-[#D4AF37] mr-3 flex-shrink-0" />}
                                  <div>
                                    <div className="font-semibold text-[#EAE7E1]">{tier.tier_name}</div>
                                    {tier.description && <div className="text-sm text-[#AFAFAF] mt-1">{tier.description}</div>}
                                    <div className="text-xs text-[#777] mt-1">
                                      {tier.attendees_per_pass > 1 ? `${tier.attendees_per_pass} attendees per pass` : '1 attendee'}
                                      {tier.remaining > 0 && ` | ${tier.remaining} left`}
                                      {tier.remaining === -1 && ` | Unlimited`}
                                    </div>
                                    {isLocked && <div className="text-xs text-[#AFAFAF] mt-1">This pass requires an access code</div>}
                                  </div>
                                </div>
                                <div className="text-right">
                                  {tier.is_comp ? (
                                    <div className="text-[#D4AF37] font-bold">FREE</div>
                                  ) : (
                                    <div className="text-xl font-bold text-[#D4AF37]">${tier.price}</div>
                                  )}
                                  {isSoldOut && <div className="text-xs text-[#C44545] font-medium mt-1">SOLD OUT</div>}
                                </div>
                              </div>
                            </button>
                            {/* Access Code Input */}
                            {tier.requires_access_code && !unlockedTiers[tier.id] && !isSoldOut && (
                              <div className="mt-2 flex space-x-2" data-testid={`access-code-section-${tier.id}`}>
                                <Input
                                  value={accessCodeInputs[tier.id] || ''}
                                  onChange={(e) => setAccessCodeInputs(prev => ({ ...prev, [tier.id]: e.target.value }))}
                                  placeholder="Enter access code"
                                  className="flex-1"
                                  data-testid={`access-code-input-${tier.id}`}
                                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleValidateAccessCode(tier.id))}
                                />
                                <Button
                                  type="button"
                                  onClick={() => handleValidateAccessCode(tier.id)}
                                  disabled={validatingAccess[tier.id]}
                                  className="bg-[#D4AF37] hover:bg-[#F2D27A] text-[#050505] rounded-sm px-4 font-bold"
                                  data-testid={`unlock-tier-button-${tier.id}`}
                                >
                                  {validatingAccess[tier.id] ? '...' : 'Unlock'}
                                </Button>
                              </div>
                            )}
                            {/* Quantity selector for selected tier */}
                            {isSelected && !isSoldOut && !tier.is_comp && tier.allow_quantity_selection !== false && (
                              <div className="mt-3 flex items-center justify-between bg-[#050505] border border-[#1A1A1A] rounded-sm p-3" data-testid={`entry-quantity-section-${tier.id}`}>
                                <span className="text-sm text-[#AFAFAF]">Quantity</span>
                                <div className="flex items-center space-x-3">
                                  <button
                                    type="button"
                                    onClick={() => setEntryQuantity(q => Math.max(getMinEntryQty(), q - 1))}
                                    className="w-8 h-8 flex items-center justify-center rounded-sm border border-[#333] text-[#AFAFAF] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
                                    data-testid="entry-qty-minus"
                                  >
                                    <Minus className="w-4 h-4" />
                                  </button>
                                  <span className="text-lg font-bold text-[#EAE7E1] w-8 text-center" data-testid="entry-qty-display">{entryQuantity}</span>
                                  <button
                                    type="button"
                                    onClick={() => setEntryQuantity(q => Math.min(getMaxEntryQty(), q + 1))}
                                    className="w-8 h-8 flex items-center justify-center rounded-sm border border-[#333] text-[#AFAFAF] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
                                    data-testid="entry-qty-plus"
                                  >
                                    <Plus className="w-4 h-4" />
                                  </button>
                                </div>
                                <span className="text-sm font-semibold text-[#D4AF37]">${(tier.price * entryQuantity).toFixed(2)}</span>
                              </div>
                            )}
                            {/* Fixed quantity indicator */}
                            {isSelected && !isSoldOut && !tier.is_comp && tier.allow_quantity_selection === false && (
                              <div className="mt-3 flex items-center justify-between bg-[#050505] border border-[#1A1A1A] rounded-sm p-3" data-testid={`entry-fixed-qty-${tier.id}`}>
                                <span className="text-xs text-[#777] uppercase tracking-wide">Includes 1</span>
                                <span className="text-sm font-semibold text-[#D4AF37]">${tier.price.toFixed(2)}</span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  /* No tiers - show payment methods directly */
                  <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-6">
                    <h3 className="text-lg font-semibold text-[#EAE7E1] mb-4 tracking-tight">Payment Methods</h3>
                    <p className="text-sm text-[#AFAFAF] mb-4">
                      Please complete your payment using one of the methods below.
                    </p>
                    <div className="space-y-3">
                      {(event.payment_links || []).map((link) => renderPaymentMethod(link))}
                    </div>
                    <Button
                      onClick={() => {
                        const methods = event.payment_links || [];
                        if (methods.length === 1) setForm(prev => ({ ...prev, payment_method_used: methods[0].method }));
                        setStep(3);
                      }}
                      className="w-full mt-6 bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#F2D27A] hover:opacity-90 text-[#050505] rounded-sm px-6 py-4 font-bold text-base"
                      data-testid="proceed-to-confirmation-button"
                    >
                      <CheckCircle className="w-5 h-5 mr-2" />
                      I've Sent Payment
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </div>
                )}

                {/* SECTION 2: Add-Ons (Optional) */}
                {activeAddons.length > 0 && (
                  <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-6" data-testid="addons-selection">
                    <div className="flex items-center space-x-2 mb-4">
                      <Package className="w-5 h-5 text-[#D4AF37]" />
                      <h3 className="text-lg font-semibold text-[#EAE7E1] tracking-tight">Add-Ons (Optional)</h3>
                    </div>
                    <div className="space-y-3">
                      {activeAddons.map(addon => {
                        const qty = selectedAddOns[addon.id] || 0;
                        const isSelected = qty > 0;
                        const maxQty = getMaxAddonQty(addon);
                        const isSoldOut = addon.remaining === 0;
                        const isFixed = addon.allow_quantity_selection === false;
                        return (
                          <div
                            key={addon.id}
                            onClick={isFixed && !isSoldOut ? () => toggleFixedAddon(addon.id) : undefined}
                            className={`border-2 rounded-sm p-4 transition-colors ${
                              isSelected ? 'border-[#D4AF37] bg-[#D4AF37]/5' :
                              isSoldOut ? 'border-[#1A1A1A] opacity-40' :
                              isFixed ? 'border-[#1A1A1A] hover:border-[#333] cursor-pointer' :
                              'border-[#1A1A1A] hover:border-[#333]'
                            }`}
                            data-testid={`addon-option-${addon.id}`}
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex items-start flex-1 min-w-0 mr-3">
                                {isFixed && (
                                  <div className={`w-5 h-5 rounded border-2 mr-3 flex-shrink-0 mt-0.5 flex items-center justify-center ${isSelected ? 'border-[#D4AF37] bg-[#D4AF37]' : 'border-[#555]'}`}>
                                    {isSelected && <svg className="w-3 h-3 text-[#050505]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                                  </div>
                                )}
                                <div className="min-w-0">
                                  <div className="font-semibold text-[#EAE7E1]">{addon.tier_name}</div>
                                  {addon.description && <div className="text-sm text-[#AFAFAF] mt-1 line-clamp-2">{addon.description}</div>}
                                  <div className="text-xs text-[#777] mt-1">
                                    {isFixed && <span className="text-[#D4AF37]">Includes 1</span>}
                                    {!isFixed && addon.remaining > 0 && `${addon.remaining} left`}
                                    {!isFixed && addon.remaining === -1 && 'Unlimited'}
                                    {isSoldOut && <span className="text-[#C44545]">SOLD OUT</span>}
                                  </div>
                                </div>
                              </div>
                              <div className="flex flex-col items-end space-y-2 flex-shrink-0">
                                <div className="text-[#D4AF37] font-bold text-lg">${addon.price}</div>
                                {!isSoldOut && !isFixed && (
                                  <div className="flex items-center space-x-2">
                                    <button
                                      type="button"
                                      onClick={() => updateAddonQty(addon.id, -1)}
                                      className="w-8 h-8 flex items-center justify-center rounded-sm border border-[#333] text-[#AFAFAF] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
                                      data-testid={`addon-qty-minus-${addon.id}`}
                                    >
                                      <Minus className="w-3 h-3" />
                                    </button>
                                    <span className="text-sm font-bold text-[#EAE7E1] w-6 text-center" data-testid={`addon-qty-display-${addon.id}`}>{qty}</span>
                                    <button
                                      type="button"
                                      onClick={() => updateAddonQty(addon.id, 1)}
                                      disabled={qty >= maxQty}
                                      className="w-8 h-8 flex items-center justify-center rounded-sm border border-[#333] text-[#AFAFAF] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors disabled:opacity-30"
                                      data-testid={`addon-qty-plus-${addon.id}`}
                                    >
                                      <Plus className="w-3 h-3" />
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                            {/* Line total when qty > 0 */}
                            {isSelected && qty > 1 && !isFixed && (
                              <div className="mt-2 pt-2 border-t border-[#1A1A1A] flex justify-end">
                                <span className="text-sm font-semibold text-[#D4AF37]">${(addon.price * qty).toFixed(2)}</span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Running Order Total */}
                {selectedTier && (
                  <div className="bg-[#0A0A0A] border border-[#D4AF37]/30 rounded-sm p-4 transition-all" data-testid="running-total">
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="text-xs text-[#777] uppercase tracking-wider">Your Order</div>
                        <div className="text-sm text-[#EAE7E1] mt-1">
                          {selectedTier.tier_name}{!selectedTier.is_comp && (selectedTier.allow_quantity_selection !== false ? ` x${entryQuantity}` : '')}
                          {Object.keys(selectedAddOns).length > 0 && ` + ${Object.keys(selectedAddOns).length} add-on${Object.keys(selectedAddOns).length > 1 ? 's' : ''}`}
                        </div>
                        {selectedTier.attendees_per_pass > 1 && (
                          <div className="text-xs text-[#AFAFAF] mt-0.5">{selectedTier.attendees_per_pass * entryQuantity} attendees</div>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-black text-[#D4AF37] transition-all" data-testid="running-total-amount" key={getDisplayPrice()}>
                          {selectedTier.is_comp ? 'FREE' : `$${getDisplayPrice().toFixed(2)}`}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Continue Button with total */}
                {hasTiers && (
                  <Button
                    onClick={proceedToPayment}
                    disabled={!selectedTier}
                    className="w-full bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#F2D27A] hover:opacity-90 text-[#050505] rounded-sm px-6 py-3 font-bold disabled:opacity-40 text-base"
                    data-testid="continue-to-payment-button"
                  >
                    {selectedTier ? (
                      <>Continue to Payment{!selectedTier.is_comp && ` \u2014 $${getDisplayPrice().toFixed(2)}`}</>
                    ) : (
                      <>Select a Pass to Continue</>
                    )}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                )}
              </>
            )}

            {/* Step 2: Payment Methods */}
            {step === 2 && (
              <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-6">
                {/* Payment Summary */}
                {renderPaymentSummary()}

                {!selectedTier?.is_comp && (
                  <>
                    {/* Step-by-step instructions for direct transfer */}
                    {renderDirectTransferSteps()}

                    {/* Discount Code */}
                    <div className="mb-5 p-3 bg-[#050505] border border-[#1A1A1A] rounded-sm" data-testid="discount-code-section">
                      <div className="flex items-center space-x-2 mb-2">
                        <Tag className="w-4 h-4 text-[#D4AF37]" />
                        <span className="text-sm font-medium text-[#AFAFAF]">Have a discount code?</span>
                      </div>
                      <div className="flex space-x-2">
                        <Input
                          value={discountCode}
                          onChange={(e) => { setDiscountCode(e.target.value); if (discountResult) setDiscountResult(null); }}
                          placeholder="Enter code"
                          className="flex-1"
                          data-testid="discount-code-input"
                          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleValidateDiscount())}
                        />
                        <Button
                          type="button"
                          onClick={handleValidateDiscount}
                          disabled={validatingDiscount}
                          variant="outline"
                          className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A] rounded-sm px-4"
                          data-testid="apply-discount-button"
                        >
                          {validatingDiscount ? '...' : 'Apply'}
                        </Button>
                      </div>
                      {discountResult?.valid && (
                        <div className="mt-2 text-sm text-[#D4AF37]" data-testid="discount-applied-message">
                          {discountResult.discount_type === 'percentage' ? `${discountResult.value}% off` : `$${discountResult.value} off`} applied — You save ${discountResult.discount_amount}
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Payment method heading */}
                <h3 className="text-lg font-semibold text-[#EAE7E1] mb-4 tracking-tight">
                  {selectedTier?.is_comp ? 'Complimentary Pass' : 'Payment Methods'}
                </h3>

                {selectedTier?.is_comp ? (
                  <div className="text-center py-4">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1a2a1a] rounded-sm mb-4">
                      <CheckCircle className="w-5 h-5 text-[#6EE7B7]" />
                      <span className="text-[#6EE7B7] font-semibold">No payment required</span>
                    </div>
                    <p className="text-sm text-[#AFAFAF] mb-4">This is a complimentary pass.</p>
                    <Button onClick={() => { setForm(prev => ({ ...prev, payment_method_used: 'Complimentary' })); setStep(3); }} className="bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#F2D27A] hover:opacity-90 text-[#050505] rounded-sm px-6 py-3 font-bold" data-testid="proceed-comp-button">
                      Continue <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </div>
                ) : (selectedTier && !selectedTier.is_comp && getDisplayPrice() > 0) ? (
                  /* Paid tier — surface whatever the host actually accepts.
                     Priority:
                       1. If host has Stripe Connect onboarded → show card button
                       2. If event has external payment_links → show those too
                          (do NOT hide them just because Stripe exists)
                       3. If neither → empty-state (host hasn't configured payments) */
                  (() => {
                    const stripeOk = !!event.host_stripe_connected;
                    const externalLinks = (event.payment_links || []).filter(l => l && l.method);
                    const hasExternal = externalLinks.length > 0;
                    if (!stripeOk && !hasExternal) {
                      return (
                        <div className="rounded-sm border border-[#C44545]/30 bg-[#C44545]/5 px-4 py-4 text-center" data-testid="no-payment-methods">
                          <p className="text-sm text-[#EAE7E1] font-semibold mb-1">Payments aren't set up yet</p>
                          <p className="text-xs text-[#AFAFAF]">The host hasn't connected card payments and no manual payment methods are listed. Please contact the host.</p>
                        </div>
                      );
                    }
                    return (
                      <div className="space-y-4">
                        {stripeOk && (
                          <div data-testid="stripe-payment-block">
                            <div className="rounded-sm border border-[#1A1A1A] bg-[#050505] px-4 py-3 mb-3">
                              <p className="text-[13px] text-[#EAE7E1] leading-relaxed">
                                Pay by card via <span className="text-[#D4AF37] font-semibold">Stripe</span>. Card details never touch VelvetGate.
                              </p>
                            </div>
                            <Button
                              onClick={() => { setForm(prev => ({ ...prev, payment_method_used: 'Stripe' })); setStep(3); }}
                              className="w-full bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#F2D27A] hover:opacity-90 text-[#050505] rounded-sm px-6 py-4 font-bold text-base"
                              data-testid="proceed-to-stripe-button"
                            >
                              Continue to Pay with Card
                              <ArrowRight className="w-5 h-5 ml-2" />
                            </Button>
                          </div>
                        )}
                        {hasExternal && (
                          <div data-testid="external-payment-block">
                            {stripeOk && (
                              <div className="flex items-center gap-3 my-3">
                                <div className="flex-1 h-px bg-[#1A1A1A]"></div>
                                <span className="text-xs text-[#777] uppercase tracking-wider">or pay manually</span>
                                <div className="flex-1 h-px bg-[#1A1A1A]"></div>
                              </div>
                            )}
                            <div className="space-y-3">
                              {externalLinks.map((link) => renderPaymentMethod(link))}
                            </div>
                            <Button
                              onClick={() => {
                                if (externalLinks.length === 1) {
                                  const m = externalLinks[0];
                                  setForm(prev => ({ ...prev, payment_method_used: m.method + (m.handle ? ` (${m.handle})` : '') }));
                                }
                                setStep(3);
                              }}
                              className="w-full mt-4 bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#F2D27A] hover:opacity-90 text-[#050505] rounded-sm px-6 py-4 font-bold text-base"
                              data-testid="proceed-to-confirmation-button"
                            >
                              <CheckCircle className="w-5 h-5 mr-2" />
                              I've Sent Payment
                              <ArrowRight className="w-5 h-5 ml-2" />
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })()
                ) : (
                  <>
                    <div className="space-y-3">
                      {(event.payment_links || []).map((link) => renderPaymentMethod(link))}
                    </div>
                    <Button
                      onClick={() => {
                        const methods = event.payment_links || [];
                        if (methods.length === 1) setForm(prev => ({ ...prev, payment_method_used: methods[0].method }));
                        setStep(3);
                      }}
                      className="w-full mt-6 bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#F2D27A] hover:opacity-90 text-[#050505] rounded-sm px-6 py-4 font-bold text-base"
                      data-testid="proceed-to-confirmation-button"
                    >
                      <CheckCircle className="w-5 h-5 mr-2" />
                      I've Sent Payment
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </>
                )}
                <Button type="button" onClick={() => { setStep(1); setDiscountCode(''); setDiscountResult(null); }} variant="ghost" className="mt-3 w-full text-[#AFAFAF] hover:text-[#EAE7E1]" data-testid="back-to-tiers-button">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back to Selection
                </Button>
              </div>
            )}

            {/* Step 3: Confirmation Form */}
            {step === 3 && !purchaseComplete && (
              <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-6">
                <h3 className="text-lg font-semibold text-[#EAE7E1] mb-4 tracking-tight">Get Your VelvetGate Pass</h3>
                {renderPaymentSummary()}
                <form onSubmit={handleSubmitTicket} className="space-y-4" data-testid="ticket-form">
                  <div>
                    <Label>Email *</Label>
                    <Input id="guest_email" type="email" value={form.guest_email} onChange={(e) => { setForm({ ...form, guest_email: e.target.value }); setEmailChecked(null); }} onBlur={(e) => handleCheckEmail(e.target.value)} required className="mt-1" data-testid="guest-email-input" />
                    {emailChecked?.exists && (
                      <p className="text-xs text-[#D4AF37] mt-1" data-testid="account-found-hint">Account found — your pass will be linked automatically</p>
                    )}
                    <p className="text-[11px] text-[#555] mt-1.5">
                      Already have a pass?{' '}
                      <a href="/find-pass" className="text-[#D4AF37] hover:underline" data-testid="event-page-find-pass-link">Find it here</a>
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="guest_name">Name(s) / Screen Name <span className="text-[#555] font-normal">(optional)</span></Label>
                    <Input id="guest_name" value={form.guest_name} onChange={(e) => setForm({ ...form, guest_name: e.target.value })} placeholder="What should appear on your pass" className="mt-1" data-testid="guest-name-input" />
                    <p className="text-xs text-[#555] mt-1">This is what appears on your pass and at check-in.</p>
                    {!showLegalName && (
                      <button type="button" onClick={() => setShowLegalName(true)} className="text-xs text-[#555] hover:text-[#AFAFAF] mt-1 transition-colors" data-testid="show-legal-name-toggle">+ Add legal name</button>
                    )}
                    {showLegalName && (
                      <div className="mt-2">
                        <Label htmlFor="guest_legal_name" className="text-xs">Legal Name <span className="text-[#555] font-normal">(optional, private)</span></Label>
                        <Input id="guest_legal_name" value={form.guest_legal_name} onChange={(e) => setForm({ ...form, guest_legal_name: e.target.value })} placeholder="Only used if required by the event" className="mt-1" data-testid="guest-legal-name-input" />
                      </div>
                    )}
                  </div>
                  {!selectedTier?.is_comp && !isCashRsvpMode && form.payment_method_used !== 'Stripe' && (
                    <div>
                      <Label>Transaction ID or Reference (optional)</Label>
                      <Input id="payment_confirmation" value={form.payment_confirmation} onChange={(e) => setForm({ ...form, payment_confirmation: e.target.value })} placeholder="e.g., Zelle conf #, Venmo note, or screenshot ref" className="mt-1" data-testid="payment-confirmation-input" />
                    </div>
                  )}
                  {!selectedTier?.is_comp && !isCashRsvpMode && form.payment_method_used !== 'Stripe' && (
                    <div>
                      <Label>Payment Proof Screenshot (optional)</Label>
                      <div className="mt-1">
                        <label className="flex items-center gap-3 p-3 border border-dashed border-[#1A1A1A] rounded-sm cursor-pointer hover:border-[#D4AF37]/30 transition-colors" data-testid="proof-upload-label">
                          <div className="flex-1">
                            {proofFile ? (
                              <span className="text-sm text-[#EAE7E1]">{proofFile.name}</span>
                            ) : (
                              <span className="text-sm text-[#555]">Choose file (JPG, PNG, WEBP)</span>
                            )}
                          </div>
                          <span className="text-xs text-[#D4AF37] font-medium">Browse</span>
                          <input type="file" accept=".jpg,.jpeg,.png,.webp" className="sr-only" onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              setProofFile(file);
                              setProofPreview(URL.createObjectURL(file));
                            }
                          }} data-testid="proof-file-input" />
                        </label>
                        {proofPreview && (
                          <div className="mt-2 relative inline-block">
                            <img src={proofPreview} alt="Payment proof" className="h-20 rounded border border-[#1A1A1A] object-cover" data-testid="proof-preview" />
                            <button type="button" onClick={() => { setProofFile(null); setProofPreview(null); }} className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-[#C44545] text-white rounded-full text-xs flex items-center justify-center" data-testid="remove-proof">×</button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {selectedTier?.is_comp && (
                    <div>
                      <Label>Confirmation Note</Label>
                      <Input id="payment_confirmation" value={form.payment_confirmation} onChange={(e) => setForm({ ...form, payment_confirmation: e.target.value })} placeholder="Invited by host" className="mt-1" data-testid="payment-confirmation-input" />
                    </div>
                  )}
                  {!selectedTier?.is_comp && !isCashRsvpMode && form.payment_method_used !== 'Stripe' && (
                    <div>
                      <Label>Which payment method did you use? *</Label>
                      <select id="payment_method_used" value={form.payment_method_used} onChange={(e) => setForm({ ...form, payment_method_used: e.target.value })} required className="w-full mt-1 px-3 py-2 rounded-sm bg-[#050505] border border-[#1A1A1A] text-[#EAE7E1] focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]" data-testid="payment-method-select">
                        <option value="">Select payment method</option>
                        {(event.payment_links || []).map((link, idx) => (
                          <option key={link.id || idx} value={link.method + (link.handle ? ` (${link.handle})` : '')}>{link.method}{link.handle ? ` (${link.handle})` : ''}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {isCashRsvpMode && (
                    <div className="rounded-sm border border-[#D4AF37]/30 bg-[#D4AF37]/5 px-4 py-3" data-testid="rsvp-mode-banner">
                      <p className="text-sm font-semibold text-[#D4AF37] mb-1">Reserve your RSVP spot</p>
                      <p className="text-[13px] text-[#AFAFAF] leading-relaxed">
                        No payment now — pay cash at the door. We'll hold your spot once you reserve.
                      </p>
                    </div>
                  )}
                  {/* How did you hear + tour request — additive */}
                  <div>
                    <Label htmlFor="how_heard_about">How did you hear about this event? <span className="text-[#555] font-normal">(optional)</span></Label>
                    <select
                      id="how_heard_about"
                      value={form.how_heard_about}
                      onChange={(e) => setForm({ ...form, how_heard_about: e.target.value })}
                      className="w-full mt-1 px-3 py-2 rounded-sm bg-[#050505] border border-[#1A1A1A] text-[#EAE7E1] focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]"
                      data-testid="how-heard-select"
                    >
                      <option value="">Prefer not to say</option>
                      {resolveSourceOptions(event).map((opt) => (
                        <option key={opt} value={opt} data-testid={`how-heard-opt-${opt.toLowerCase().replace(/\s+/g, '-')}`}>{opt}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="needs-tour-toggle-label">
                      <input
                        type="checkbox"
                        checked={!!form.needs_tour}
                        onChange={(e) => setForm({ ...form, needs_tour: e.target.checked })}
                        className="rounded border-[#1A1A1A]"
                        data-testid="needs-tour-checkbox"
                      />
                      <span className="text-sm text-[#AFAFAF]">First time? Request a quick tour at arrival</span>
                    </label>
                  </div>
                  {form.payment_method_used === 'Stripe' && !isCashRsvpMode && (
                    <div className="rounded-sm border border-[#1A1A1A] bg-[#050505] px-4 py-3" data-testid="stripe-info-banner">
                      <p className="text-[13px] text-[#EAE7E1] leading-relaxed">
                        On submit, you'll be sent to <span className="text-[#D4AF37] font-semibold">Stripe</span> to enter your card details. We never store your card.
                      </p>
                    </div>
                  )}
                  <div className="flex space-x-3">
                    <Button type="button" onClick={() => setStep(hasTiers ? 2 : 1)} variant="outline" className="flex-1 border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A] rounded-sm px-6 py-3" data-testid="back-button">
                      Back
                    </Button>
                    <Button type="submit" className="flex-1 bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#F2D27A] hover:opacity-90 text-[#050505] rounded-sm px-6 py-3 font-bold" disabled={submitting} data-testid="get-ticket-button">
                      {submitting
                        ? (form.payment_method_used === 'Stripe' && !isCashRsvpMode ? 'Redirecting…' : 'Processing...')
                        : (isCashRsvpMode
                            ? 'Reserve My RSVP Spot'
                            : (form.payment_method_used === 'Stripe'
                                ? `Pay $${getDisplayPrice().toFixed(2)} with Card`
                                : getEventPersonality(event.event_type).labels.ctaPrimary))}
                    </Button>
                  </div>
                </form>
              </div>
            )}

            {/* Post-purchase: Order Confirmation (always shown when purchase completes;
                renders ABOVE ClaimAccountCard / ClaimSuccessCard, replaces neither) */}
            {step === 3 && purchaseComplete && (
              <OrderConfirmationCard
                event={event}
                selectedTier={selectedTier}
                entryQuantity={entryQuantity}
                selectedAddOns={selectedAddOns}
                activeAddOns={activeAddons}
                purchaseEmail={purchaseComplete.email}
                ticketId={purchaseComplete.ticket_id}
                onViewPass={() => navigate(`/ticket/${purchaseComplete.ticket_id}`)}
                onGoToMyPasses={() => navigate('/my-tickets')}
              />
            )}

            {/* Post-purchase: Account Claim */}
            {step === 3 && purchaseComplete && !accountClaimed && (
              <ClaimAccountCard
                claimForm={claimForm}
                setClaimForm={setClaimForm}
                onClaim={handleClaimAccount}
                claiming={claiming}
                purchaseEmail={purchaseComplete.email}
                ticketId={purchaseComplete.ticket_id}
                navigate={navigate}
                isManual={event?.release_method === 'manual'}
              />
            )}

            {/* Post-claim: Success with clear CTAs */}
            {step === 3 && purchaseComplete && accountClaimed && (
              <ClaimSuccessCard
                onGoToMyPasses={() => navigate('/my-tickets')}
                onViewThisPass={() => navigate(`/ticket/${purchaseComplete.ticket_id}`)}
              />
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <EventSidebar event={event} availability={availability} />
          </div>
        </div>
      </div>
    </div>
  );
}
